/**
 * AI Topic Restrictions Component
 * TASK-335: AI Assistant Configuration & Restrictions
 *
 * Custom topic restriction CRUD.
 */

import React, { useState } from 'react';
import { Plus, Trash2, AlertTriangle, Ban, Info, AlertCircle } from 'lucide-react';
import type { TopicRestriction, TopicRestrictionCreateData, RestrictionType } from '../../../types/admin';
import { RESTRICTION_TYPE_LABELS, RESTRICTION_TYPE_DESCRIPTIONS } from '../../../types/admin';
import { aiSettingsService } from '../../../services/admin';
import { Button } from '../../../components/ui/button';
import { toast } from 'sonner';
import { extractErrorMessage } from '../../../utils/apiErrorHandler';

interface AITopicRestrictionsProps {
  restrictions: TopicRestriction[];
  onRestrictionsChange: () => void;
}

const RESTRICTION_TYPES: RestrictionType[] = ['forbidden', 'disclaimer_required', 'limited'];

const RESTRICTION_ICONS: Record<RestrictionType, React.ReactNode> = {
  forbidden: <Ban size={14} className="text-red-600" />,
  disclaimer_required: <AlertTriangle size={14} className="text-amber-600" />,
  limited: <Info size={14} className="text-blue-600" />,
};

const RESTRICTION_COLORS: Record<RestrictionType, string> = {
  forbidden: 'bg-red-50 border-red-200 text-red-800',
  disclaimer_required: 'bg-amber-50 border-amber-200 text-amber-800',
  limited: 'bg-blue-50 border-blue-200 text-blue-800',
};

export const AITopicRestrictions: React.FC<AITopicRestrictionsProps> = ({
  restrictions,
  onRestrictionsChange,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [newRestriction, setNewRestriction] = useState<TopicRestrictionCreateData>({
    topic: '',
    restriction_type: 'forbidden',
    custom_response: '',
  });

  const handleAdd = async () => {
    if (!newRestriction.topic.trim()) {
      toast.error('Please enter a topic');
      return;
    }

    setIsAdding(true);
    try {
      await aiSettingsService.addTopicRestriction({
        ...newRestriction,
        topic: newRestriction.topic.trim(),
        custom_response: newRestriction.custom_response?.trim() || undefined,
      });
      toast.success('Topic restriction added');
      setNewRestriction({ topic: '', restriction_type: 'forbidden', custom_response: '' });
      setShowAddForm(false);
      onRestrictionsChange();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to add restriction'));
    } finally {
      setIsAdding(false);
    }
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await aiSettingsService.removeTopicRestriction(id);
      toast.success('Topic restriction removed');
      onRestrictionsChange();
    } catch (error) {
      toast.error(extractErrorMessage(error, 'Failed to remove restriction'));
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Topic Restrictions</h3>
          <p className="text-sm text-gray-500 mt-1">
            Define topics the AI should avoid or handle with care.
          </p>
        </div>
        <Button onClick={() => setShowAddForm(true)} disabled={showAddForm}>
          <Plus size={16} className="mr-2" />
          Add Restriction
        </Button>
      </div>

      {/* Add Form */}
      {showAddForm && (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg space-y-4">
          <h4 className="font-medium text-gray-900">New Topic Restriction</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Topic</label>
            <input
              type="text"
              value={newRestriction.topic}
              onChange={(e) => setNewRestriction({ ...newRestriction, topic: e.target.value })}
              placeholder="e.g., competitor products, internal salary information"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Restriction Type</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {RESTRICTION_TYPES.map((type) => (
                <label
                  key={type}
                  className={`
                    p-3 rounded-lg border cursor-pointer transition-colors
                    ${newRestriction.restriction_type === type ? 'border-[#002B45] bg-blue-50' : 'border-gray-200 hover:border-gray-300'}
                  `}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <input
                      type="radio"
                      name="restriction_type"
                      value={type}
                      checked={newRestriction.restriction_type === type}
                      onChange={() => setNewRestriction({ ...newRestriction, restriction_type: type })}
                      className="text-[#002B45] focus:ring-[#F7B500]"
                    />
                    <span className="font-medium text-gray-900 text-sm">
                      {RESTRICTION_TYPE_LABELS[type]}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 ml-6">
                    {RESTRICTION_TYPE_DESCRIPTIONS[type]}
                  </p>
                </label>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Custom Response (Optional)
            </label>
            <textarea
              value={newRestriction.custom_response || ''}
              onChange={(e) =>
                setNewRestriction({ ...newRestriction, custom_response: e.target.value })
              }
              placeholder="What should the AI say when this topic comes up? Leave empty for default response."
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#F7B500]"
            />
          </div>

          <div className="flex items-center justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowAddForm(false);
                setNewRestriction({ topic: '', restriction_type: 'forbidden', custom_response: '' });
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleAdd} disabled={isAdding || !newRestriction.topic.trim()}>
              {isAdding ? 'Adding...' : 'Add Restriction'}
            </Button>
          </div>
        </div>
      )}

      {/* Restrictions List */}
      {restrictions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <AlertCircle size={40} className="mx-auto text-gray-300 mb-2" />
          <p>No topic restrictions configured</p>
          <p className="text-sm">Add restrictions for topics the AI should avoid or handle carefully.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {restrictions.map((restriction) => (
            <div
              key={restriction.id}
              className={`
                flex items-start justify-between p-4 rounded-lg border
                ${RESTRICTION_COLORS[restriction.restriction_type]}
              `}
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">{RESTRICTION_ICONS[restriction.restriction_type]}</div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{restriction.topic}</span>
                    <span className="text-xs px-2 py-0.5 rounded bg-white/50">
                      {RESTRICTION_TYPE_LABELS[restriction.restriction_type]}
                    </span>
                  </div>
                  {restriction.custom_response && (
                    <p className="text-sm mt-1 opacity-80">"{restriction.custom_response}"</p>
                  )}
                  {restriction.created_at && (
                    <p className="text-xs mt-1 opacity-60">
                      Added {new Date(restriction.created_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <button
                onClick={() => handleDelete(restriction.id)}
                disabled={deletingId === restriction.id}
                className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                title="Remove restriction"
              >
                {deletingId === restriction.id ? (
                  <div className="w-4 h-4 border-2 border-gray-300 border-t-gray-600 rounded-full animate-spin" />
                ) : (
                  <Trash2 size={16} />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Suggested Topics */}
      <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
        <h4 className="text-sm font-medium text-gray-700 mb-2">Common Restricted Topics</h4>
        <div className="flex flex-wrap gap-2">
          {[
            'Competitor products',
            'Internal salaries',
            'Confidential M&A',
            'Political opinions',
            'Medical diagnoses',
            'Speculation',
          ].map((topic) => (
            <button
              key={topic}
              onClick={() => {
                setNewRestriction({ ...newRestriction, topic });
                setShowAddForm(true);
              }}
              className="px-3 py-1 text-xs bg-white border border-gray-300 rounded-full hover:bg-gray-100 transition-colors"
            >
              + {topic}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AITopicRestrictions;
